import React, { useState, useEffect } from "react";
import { Modal, Button, Select, Row, Col, Typography, message } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import "./TimeSelect.css";

const { Option } = Select;

const TimeSelect = ({
  show,
  handleClose,
  onSelectTime,
  selectedTime,
  selectedDate,
}) => {
  const [selectedHour, setSelectedHour] = useState("");
  const [selectedMinute, setSelectedMinute] = useState("");
  const [selectedDuration, setSelectedDuration] = useState("");
  const [endTime, setEndTime] = useState({ hour: "", minute: "" });
  const [currentTime, setCurrentTime] = useState(getCurrentTime());

  const hours = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, "0")
  );
  const minutes = ["00", "15", "30", "45"];
  const presetTimes = [
    "Now",
    "08:00",
    "10:00",
    "12:00",
    "14:00",
    "16:00",
    "18:00",
  ];
  const durations = [
    "No duration",
    "30 Minute",
    "45 Minute",
    "1 Hour",
    "1 Hour 30 Minute",
    "2 Hour",
  ];
  function getDurationLabel(minutes) {
    switch (minutes) {
      case 30:
        return "30 Minute";
      case 45:
        return "45 Minute";
      case 60:
        return "1 Hour";
      case 90:
        return "1 Hour 30 Minute";
      case 120:
        return "2 Hour";
      default:
        return "No duration";
    }
  }
  useEffect(() => {
    if (selectedTime?.start && selectedTime?.end) {
      // console.log("mytime>>>", selectedTime.end);

      const [startHour, startMinute] = selectedTime.start.split(":");
      const [endHour, endMinute] = selectedTime.end.split(":");

      setSelectedHour(startHour);
      setSelectedMinute(startMinute);
      setEndTime({ hour: endHour, minute: endMinute });

      // Automatically set the duration based on time difference
      const durationMinutes =
        parseInt(endHour) * 60 +
        parseInt(endMinute) -
        (parseInt(startHour) * 60 + parseInt(startMinute));
      setSelectedDuration(getDurationLabel(durationMinutes));
    }
  }, [selectedTime]);

  useEffect(() => {
    if (!selectedHour || !selectedMinute) {
      return;
    }

    const durationIsUnset = selectedDuration === "No duration";
    const hasAnyEndSelection = endTime.hour || endTime.minute;

    // Respect a user-selected end time (even partial) when no duration is set
    if (durationIsUnset && hasAnyEndSelection) {
      return;
    }

    setEndTime(
      calculateEndTime(selectedHour, selectedMinute, selectedDuration)
    );
  }, [selectedHour, selectedMinute, selectedDuration]);
  // useEffect(() => {
  //   if (selectedHour && selectedMinute && endTime.hour && endTime.minute) {
  //     updateDuration(
  //       selectedHour,
  //       selectedMinute,
  //       endTime.hour,
  //       endTime.minute
  //     );
  //   }
  // }, [selectedHour, selectedMinute, endTime]);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(getCurrentTime());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  function getCurrentTime() {
    const now = new Date();
    return {
      hour: now.getHours().toString().padStart(2, "0"),
      minute: now.getMinutes().toString().padStart(2, "0"),
    };
  }
  function updateDuration(startHour, startMinute, endHour, endMinute) {
    if (!startHour || !startMinute || !endHour || !endMinute) {
      setSelectedDuration("No duration");
      return;
    }

    const startTime = parseInt(startHour) * 60 + parseInt(startMinute);
    const endTime = parseInt(endHour) * 60 + parseInt(endMinute);
    const durationMinutes = endTime - startTime;

    if (durationMinutes <= 0 || endTime > 1439) {
      // Ensure valid time and not exceeding 23:00
      setSelectedDuration("No duration");
      return;
    }

    // Match the calculated duration with predefined labels
    const matchedDuration = getDurationLabel(durationMinutes);

    if (durations.includes(matchedDuration)) {
      setSelectedDuration(matchedDuration);
    } else {
      setSelectedDuration("No duration");
    }
  }

  function calculateEndTime(hour, minute, duration) {
    let totalMinutes = parseInt(hour) * 60 + parseInt(minute);

    switch (duration) {
      case "30 Minute":
        totalMinutes += 30;
        break;
      case "45 Minute":
        totalMinutes += 45;
        break;
      case "1 Hour":
        totalMinutes += 60;
        break;
      case "1 Hour 30 Minute":
        totalMinutes += 90;
        break;
      case "2 Hour":
        totalMinutes += 120;
        break;
      case "No duration": // If "No duration" is selected, set end time to 23:00
        // return { hour: "23", minute: "00" };
        return { hour: "23", minute: "59" };
      default:
        return { hour: "", minute: "" };
    }

    if (totalMinutes >= 24 * 60) {
      // Cap to same-day last minute
      return { hour: "23", minute: "59" };
    }

    return {
      hour: (Math.floor(totalMinutes / 60) % 24).toString().padStart(2, "0"),
      minute: (totalMinutes % 60).toString().padStart(2, "0"),
    };
  }

  function handleTimeSelection(time) {
    if (time === "Now") {
      setSelectedHour(currentTime.hour);
      setSelectedMinute(currentTime.minute);
      setEndTime(
        calculateEndTime(currentTime.hour, currentTime.minute, selectedDuration)
      );
    } else {
      const [hour, minute] = time.split(":");
      setSelectedHour(hour);
      setSelectedMinute(minute);
      setEndTime(calculateEndTime(hour, minute, selectedDuration));
    }
  }

  function handleSave() {
    if (!selectedHour || !selectedMinute) {
      message.error("Please select a valid time.");
      return;
    }

    const finalEndTime =
      endTime.hour && endTime.minute ? endTime : { hour: "23", minute: "59" };

    const startMinutes =
      parseInt(selectedHour, 10) * 60 + parseInt(selectedMinute, 10);
    const endMinutes =
      parseInt(finalEndTime.hour, 10) * 60 + parseInt(finalEndTime.minute, 10);

    if (endMinutes <= startMinutes) {
      message.error("End time must be after start time.");
      return;
    }

    onSelectTime({
      start: `${selectedHour}:${selectedMinute}`,
      end: `${finalEndTime.hour}:${finalEndTime.minute}`,
    });

    message.success("Time added successfully");
    handleClose();
  }

  function isFutureDate(selectedHour, selectedMinute, selectedDate) {
    const now = new Date();
    const selectedDateTime = new Date(selectedDate);
    selectedDateTime.setHours(
      parseInt(selectedHour, 10),
      parseInt(selectedMinute, 10),
      0,
      0
    );

    return selectedDateTime >= now;
  }
  return (
    <Modal
      title=" "
      open={show}
      onCancel={handleClose}
      footer={null}
      width={500}
      centered
    >
      <Typography.Title
        level={4}
        style={{ display: "flex", alignItems: "center", gap: "8px" }}
      >
        <ClockCircleOutlined /> Select time
      </Typography.Title>

      {/* Start and End Time Dropdowns */}
      <Row gutter={8} justify="center" style={{ marginBottom: 4 }}>
        <Col span={11} style={{ textAlign: "center" }}>
          <Typography.Text strong>Start time</Typography.Text>
        </Col>
        <Col span={2}></Col>
        <Col span={11} style={{ textAlign: "center" }}>
          <Typography.Text strong>End time</Typography.Text>
        </Col>
      </Row>
      <Row className="timepicker-row" gutter={8} justify="center" style={{ marginBottom: 16 }}>
        <Col span={5}>
          <Select
            className="slectTimeSlot"
            value={selectedHour || undefined}
            onChange={(value) => {
              setSelectedHour(value);
              if (!selectedMinute) {
                setSelectedMinute("00");
              }
            }}
            placeholder="HH"
            style={{ width: "100%" }}
          >
            {/* {hours.map((hour) => (
    <Option key={hour} value={hour} disabled={!isFutureDate(hour, "00", selectedDate)}>
      {hour}
    </Option>
  ))} */}
            {hours
              .filter((hour) => {
                // If no selectedDate yet, just show all hours
                if (!selectedDate) return true;
                return isFutureDate(hour, "00", selectedDate);
              })
              .map((hour) => (
                <Option key={hour} value={hour}>
                  {hour}
                </Option>
              ))}
          </Select>
        </Col>
        <Col span={1} style={{ textAlign: "center" }}>
          :
        </Col>
        <Col span={5}>
          <Select
            className="slectTimeSlot"
            value={selectedMinute || undefined}
            onChange={(value) => {
              setSelectedMinute(value);
              updateDuration(selectedHour, value, endTime.hour, endTime.minute);
            }}
            placeholder="MM"
            style={{ width: "100%" }}
          >
            {minutes.map((minute) => (
              <Option key={minute} value={minute}>
                {minute}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={2} style={{ textAlign: "center" }}>
          -
        </Col>
        <Col span={5}>
          <Select
            className="slectTimeSlot"
            value={endTime.hour || undefined}
            onChange={(value) => {
              setEndTime({ ...endTime, hour: value });
              updateDuration(
                selectedHour,
                selectedMinute,
                value,
                endTime.minute
              );
            }}
            placeholder="HH"
            style={{ width: "100%" }}
          >
            {hours.map((hour) => (
              <Option key={hour} value={hour}>
                {hour}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={1} style={{ textAlign: "center" }}>
          :
        </Col>
        <Col span={5}>
          <Select
            className="slectTimeSlot"
            value={endTime.minute || undefined}
            onChange={(value) => {
              setEndTime({ ...endTime, minute: value });
              updateDuration(selectedHour, selectedMinute, endTime.hour, value);
            }}
            placeholder="MM"
            style={{ width: "100%" }}
          >
            {minutes.map((minute) => (
              <Option key={minute} value={minute}>
                {minute}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      {/* Preset Time Selection */}
      <Typography.Text strong>Pick a start time</Typography.Text>
      <Row gutter={[8, 8]} style={{ marginTop: 8, marginBottom: 16 }}>
        {presetTimes.map((time) => (
          <Col key={time}>
            <Button
              className={`selectTimeBtnTask ${
                selectedHour + ":" + selectedMinute ===
                (time === "Now"
                  ? `${currentTime.hour}:${currentTime.minute}`
                  : time)
                  ? "active"
                  : ""
              }`}
              type={
                selectedHour + ":" + selectedMinute ===
                (time === "Now"
                  ? `${currentTime.hour}:${currentTime.minute}`
                  : time)
                  ? "primary"
                  : "default"
              }
              onClick={() => handleTimeSelection(time)}
            >
              {time === "Now"
                ? `Now (${currentTime.hour}:${currentTime.minute})`
                : time}
            </Button>
          </Col>
        ))}
      </Row>

      {/* Duration Selection */}
      <Typography.Text strong>Duration</Typography.Text>
      <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
        {durations.map((duration) => (
          <Col key={duration}>
            <Button
              className={`selectTimeBtnTask ${
                selectedDuration === duration ? "active" : ""
              }`}
              type={selectedDuration === duration ? "primary" : "default"}
              onClick={() => {
                setSelectedDuration(duration);
                setEndTime(
                  calculateEndTime(selectedHour, selectedMinute, duration)
                );
              }}
            >
              {duration}
            </Button>
          </Col>
        ))}
      </Row>

      <Button
        type="primary"
        block
        style={{ marginTop: 16, background: "#9B51E0" }}
        onClick={handleSave}
      >
        Save
      </Button>
    </Modal>
  );
};

export default TimeSelect;
